import { useState, useEffect, useRef, useCallback } from 'react';

const PING_ENDPOINT = '/favicon.ico';
const PROBE_INTERVAL = 10000;
const LATENCY_SAMPLES = 3;

const classifyLatency = (ms) => {
  if (ms === null) return 'offline';
  if (ms < 150) return 'excellent';
  if (ms < 400) return 'good';
  if (ms < 800) return 'slow';
  return 'poor';
};

const measureLatency = async () => {
  const start = performance.now();
  try {
    await fetch(PING_ENDPOINT, {
      method: 'HEAD',
      cache: 'no-store',
      signal: AbortSignal.timeout(5000),
    });
    return Math.round(performance.now() - start);
  } catch {
    return null;
  }
};

const getConnectionSpeed = () => {
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!conn) return { downlink: null, effectiveType: null };
  return {
    downlink: typeof conn.downlink === 'number' ? conn.downlink : null,
    effectiveType: conn.effectiveType || null,
  };
};

/**
 * Monitors network quality via periodic latency probes + navigator.onLine + Network Information API.
 *
 * Returns:
 *   status         – 'excellent' | 'good' | 'slow' | 'poor' | 'offline'
 *   latency        – average round-trip ms (null when offline)
 *   downlink       – estimated Mbps from Network Information API (null if unavailable)
 *   effectiveType  – '4g' | '3g' | '2g' | 'slow-2g' (null if unavailable)
 */
const useNetworkQuality = () => {
  const [status, setStatus] = useState(() =>
    navigator.onLine ? 'good' : 'offline'
  );
  const [latency, setLatency] = useState(null);
  const [downlink, setDownlink] = useState(() => getConnectionSpeed().downlink);
  const [effectiveType, setEffectiveType] = useState(() => getConnectionSpeed().effectiveType);

  const samplesRef = useRef([]);
  const probeTimerRef = useRef(null);
  const mountedRef = useRef(true);

  const syncConnectionInfo = useCallback(() => {
    const info = getConnectionSpeed();
    setDownlink(info.downlink);
    setEffectiveType(info.effectiveType);
  }, []);

  const probe = useCallback(async () => {
    syncConnectionInfo();

    if (!navigator.onLine) {
      if (mountedRef.current) {
        setStatus('offline');
        setLatency(null);
        samplesRef.current = [];
      }
      return;
    }

    const ms = await measureLatency();
    if (!mountedRef.current) return;

    if (ms === null) {
      setStatus('offline');
      setLatency(null);
      samplesRef.current = [];
      return;
    }

    const samples = samplesRef.current;
    samples.push(ms);
    if (samples.length > LATENCY_SAMPLES) samples.shift();

    const avg = Math.round(samples.reduce((a, b) => a + b, 0) / samples.length);
    setLatency(avg);
    setStatus(classifyLatency(avg));
  }, [syncConnectionInfo]);

  const scheduleProbe = useCallback(() => {
    clearTimeout(probeTimerRef.current);
    probeTimerRef.current = setTimeout(async () => {
      await probe();
      if (mountedRef.current) scheduleProbe();
    }, PROBE_INTERVAL);
  }, [probe]);

  useEffect(() => {
    mountedRef.current = true;

    const handleOnline = () => {
      samplesRef.current = [];
      probe().then(() => { if (mountedRef.current) scheduleProbe(); });
    };
    const handleOffline = () => {
      clearTimeout(probeTimerRef.current);
      if (mountedRef.current) {
        setStatus('offline');
        setLatency(null);
        samplesRef.current = [];
      }
    };

    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const handleConnectionChange = () => { if (mountedRef.current) syncConnectionInfo(); };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    if (conn) conn.addEventListener('change', handleConnectionChange);

    probe().then(() => { if (mountedRef.current) scheduleProbe(); });

    return () => {
      mountedRef.current = false;
      clearTimeout(probeTimerRef.current);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (conn) conn.removeEventListener('change', handleConnectionChange);
    };
  }, [probe, scheduleProbe, syncConnectionInfo]);

  return { status, latency, downlink, effectiveType };
};

export default useNetworkQuality;
